import * as Apollo from '@apollo/client';

import {
    useGetActiveRecipientsAggregationsQuery,
    GetActiveRecipientsAggregationsQuery,
    GetActiveRecipientsAggregationsQueryVariables,
    useGetAllRecipientsDataQuery,
    useGetAudienceDataQuery,
    useGetCalculateRecipientsSizeQuery,
    useGetAudienceCombinationRecipientsCountQuery,
    RecipientsFilter,
    useGetRecipientsIncludingInactiveQuery
} from '../../../../generated/apollo-hooks';
import { useSelector } from '../../../infrastructure/store.interface';
import _omit from 'lodash/omit';
import { useMemo } from 'react';

export const useActiveRecipientsAggregations = (
    baseOptions?: Apollo.QueryHookOptions<GetActiveRecipientsAggregationsQuery, Omit<GetActiveRecipientsAggregationsQueryVariables, 'systemId'>>
) => {
    const systemId = useSelector(store => store.common.currentSystem);
    const { data, loading, error } = useGetActiveRecipientsAggregationsQuery({
        ...baseOptions,
        variables: { ...baseOptions?.variables, systemId }
    } as $TSFixMe);
    const aggregationsProperties = data?.getRecipientsAggregations?.propertiesAggregations;
    return { aggregationsProperties, loading, error };
};

export const useAllRecipientsData = () => {
    const systemId = useSelector(store => store.common.currentSystem);

    const { data, ...rest } = useGetAllRecipientsDataQuery({
        variables: { systemId }
    });

    return { allRecipientsData: data?.getAllRecipientsData, ...rest };
};

export function useAllRecipiensCount({
    audiencesIds,
    endUsersIds,
    isAllRecipients = false
}: {
    audiencesIds?: string[];
    endUsersIds?: string[];
    isAllRecipients?: boolean;
}) {
    const isAudienceCombinationRecipients = (audiencesIds?.length ?? 0) > 1 || (endUsersIds?.length ?? 0) > 0;
    const systemId = useSelector(store => store.common.currentSystem);
    const { data: audienceData, error: getAudienceDataError, loading: audienceDataLoading } = useGetAudienceDataQuery({
        variables: { audienceId: audiencesIds?.[0] as string, systemId },
        skip: isAllRecipients || isAudienceCombinationRecipients
    });
    const rules = audienceData?.getAudience?.data?.rules;

    const {
        data: recipientsSize,
        error: getCalculateRecipientsSizeError,
        loading: calculateRecipientsSizeLoading
    } = useGetCalculateRecipientsSizeQuery({
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        variables: { systemId, rules: rules?.map?.(rule => _omit(rule, 'role', 'integrationType')) },
        skip: isAllRecipients || isAudienceCombinationRecipients || !rules
    });

    const {
        data: allRecipientsData,
        error: GetAllRecipientsDataQueryError,
        loading: getAllRecipientsDataQueryLoading
    } = useGetAllRecipientsDataQuery({
        variables: { systemId },
        skip: !isAllRecipients
    });

    const {
        data: audienceCombinationRecipientsData,
        error: getAudienceCombinationRecipientsError,
        loading: audienceCombinationRecipientsDataLoading
    } = useGetAudienceCombinationRecipientsCountQuery({
        variables: {
            systemId,
            audienceIds: audiencesIds || [],
            endUsersIds
        },
        skip: !isAudienceCombinationRecipients
    });
    const recipientsCount = useMemo(() => {
        if (isAllRecipients) {
            const { activeEndUsersCount: active, endUsersCount: all, inactiveEndUsersCount: inactive, potentialEndUsersCount: potential } =
                allRecipientsData?.getAllRecipientsData ?? {};
            return {
                all,
                active,
                inactive,
                potential
            };
        }
        if (isAudienceCombinationRecipients) {
            return audienceCombinationRecipientsData?.getAudienceCombinationRecipientsCount;
        }

        return recipientsSize?.getCalculateRecipientsSize;
    }, [
        allRecipientsData?.getAllRecipientsData,
        audienceCombinationRecipientsData?.getAudienceCombinationRecipientsCount,
        isAllRecipients,
        isAudienceCombinationRecipients,
        recipientsSize?.getCalculateRecipientsSize
    ]);

    if (isAllRecipients) {
        return {
            loading: getAllRecipientsDataQueryLoading,
            data: recipientsCount,
            error: GetAllRecipientsDataQueryError
        };
    } else if (isAudienceCombinationRecipients) {
        return {
            loading: audienceCombinationRecipientsDataLoading,
            data: recipientsCount,
            error: getAudienceCombinationRecipientsError
        };
    }
    return {
        loading: audienceDataLoading || calculateRecipientsSizeLoading,
        data: recipientsCount,
        error: getAudienceDataError || getCalculateRecipientsSizeError
    };
}

export const useRecipientsSearch = (search: string) => {
    const { data, error, loading } = useGetRecipientsIncludingInactiveQuery({
        variables: {
            query: {
                search,
                page: 0,
                limit: 1000,
                recipientsFilter: RecipientsFilter.All
            }
        }
    });
    return { data, error, loading };
};
